import React, { useState, useEffect, useLayoutEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  Avatar,
  Divider,
  CircularProgress,
  TablePagination,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  DirectionsCar as CarIcon,
  LocalGasStation as FuelIcon,
  CalendarToday as CalendarIcon,
  ConfirmationNumber as NumberIcon,
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import LoadingBackDrop from "../global/LoadingBackDrop";
import { getPayload, setPostPayload, setPutPayload } from "../../services/api";

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 600,
  backgroundColor: status === "active" ? "#e8f5e9" : "#ffebee",
  color: status === "active" ? "#2e7d32" : "#c62828",
  "& .MuiChip-icon": {
    color: status === "active" ? "#2e7d32" : "#c62828",
  },
}));

const VehicleManagement = ({ loggedUser }) => {
  // State variables
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState("all");

  // Form state
  const [formData, setFormData] = useState({
    vehicleType: "",
    model: "",
    year: "",
    number: "",
    fuelType: "",
    assignedDriver: "",
    driverContact: "",
    status: "active",
    remarks: "",
  });

  // Vehicle types and fuel types options
  const vehicleTypes = [
    "Car",
    "Van",
    "Truck",
    "Bus",
    "Motorcycle",
    "SUV",
    "Pickup",
    "Mini Truck",
    "Three Wheeler",
    "Other",
  ];

  const fuelTypes = ["Petrol", "Diesel", "Electric", "Hybrid", "CNG", "LPG"];

  // Load vehicles on component mount
  useLayoutEffect(() => {
    loadVehicles();
  }, []);

  // Load vehicles from API
  const loadVehicles = async () => {
    setLoading(true);
    try {
      await getPayload
        .getPayloadData("/vehicle/all")
        .then(function (response) {
          console.log("Vehicles data:", response.data);
          setVehicles(response.data || []);
          setLoading(false);
        })
        .catch(function (error) {
          console.error("Error loading vehicles:", error);
          setSnackbar({
            open: true,
            message: "Failed to load vehicles",
            severity: "error",
          });
          setLoading(false);
        });
    } catch (error) {
      console.error("Error:", error);
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle switch change for status
  const handleStatusChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      status: e.target.checked ? "active" : "inactive",
    }));
  };

  // Open dialog for adding new vehicle
  const handleAddVehicle = () => {
    setIsEditMode(false);
    setSelectedVehicle(null);
    setFormData({
      vehicleType: "",
      model: "",
      year: "",
      number: "",
      fuelType: "",
      assignedDriver: "",
      driverContact: "",
      status: "active",
      remarks: "",
    });
    setOpenDialog(true);
  };

  // Open dialog for editing vehicle
  const handleEditVehicle = (vehicle) => {
    setIsEditMode(true);
    setSelectedVehicle(vehicle);
    setFormData({
      vehicleType: vehicle.vehicleType || "",
      model: vehicle.model || "",
      year: vehicle.year || "",
      number: vehicle.number || "",
      fuelType: vehicle.fuelType || "",
      assignedDriver: vehicle.assignedDriver || "",
      driverContact: vehicle.driverContact || "",
      status: vehicle.status || "active",
      remarks: vehicle.remarks || "",
    });
    setOpenDialog(true);
  };

  // Open delete confirmation dialog
  const handleDeleteClick = (vehicle) => {
    setSelectedVehicle(vehicle);
    setOpenDeleteDialog(true);
  };

  // Save vehicle (add or update)
  const handleSaveVehicle = async () => {
    // Validation
    if (!formData.vehicleType || !formData.model || !formData.number) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields",
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      let response;
      if (isEditMode && selectedVehicle) {
        // Update existing vehicle
        response = await setPutPayload.setPayloadData(
          `/vehicle/update/${selectedVehicle.id}`,
          formData,
        );
      } else {
        // Add new vehicle
        console.log("data", formData);
        response = await setPostPayload.setPayloadData(
          "/vehicle/save",
          formData,
        );
      }

      console.log("Save response:", response.data);
      setSnackbar({
        open: true,
        message: isEditMode
          ? "Vehicle updated successfully!"
          : "Vehicle added successfully!",
        severity: "success",
      });
      setOpenDialog(false);
      loadVehicles(); // Reload the list
    } catch (error) {
      console.error("Error saving vehicle:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to save vehicle",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete vehicle (set inactive)
  const handleDeleteVehicle = async () => {
    if (!selectedVehicle) return;

    setLoading(true);
    try {
      const response = await setPutPayload.setPayloadData(
        `/vehicle/update/${selectedVehicle.id}`,
        { ...selectedVehicle, status: "inactive" },
      );

      setSnackbar({
        open: true,
        message: "Vehicle deactivated successfully!",
        severity: "success",
      });
      setOpenDeleteDialog(false);
      loadVehicles(); // Reload the list
    } catch (error) {
      console.error("Error deleting vehicle:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message || "Failed to deactivate vehicle",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Toggle vehicle status (active/inactive)
  const handleToggleStatus = async (vehicle) => {
    const newStatus = vehicle.status === "active" ? "inactive" : "active";
    setLoading(true);
    try {
      const response = await setPutPayload.setPayloadData(
        `/vehicle/update/${vehicle.id}`,
        { ...vehicle, status: newStatus },
      );

      setSnackbar({
        open: true,
        message: `Vehicle ${newStatus === "active" ? "activated" : "deactivated"} successfully!`,
        severity: "success",
      });
      loadVehicles(); // Reload the list
    } catch (error) {
      console.error("Error toggling status:", error);
      setSnackbar({
        open: true,
        message: "Failed to update vehicle status",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter vehicles based on search and status
  const filteredVehicles = vehicles.filter((vehicle) => {
    const matchesSearch =
      vehicle.vehicleType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.year?.toString().includes(searchTerm) ||
      vehicle.assignedDriver
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      vehicle.driverContact?.includes(searchTerm);

    const matchesStatus =
      filterStatus === "all" || vehicle.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  // Pagination
  const paginatedVehicles = filteredVehicles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Statistics
  const totalVehicles = vehicles.length;
  const activeVehicles = vehicles.filter((v) => v.status === "active").length;
  const inactiveVehicles = vehicles.filter(
    (v) => v.status === "inactive",
  ).length;
  const vehiclesWithDriver = vehicles.filter((v) => v.assignedDriver).length;

  // Group by vehicle type for statistics
  const vehicleTypeStats = vehicles.reduce((acc, vehicle) => {
    const type = vehicle.vehicleType || "Other";
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  return (
    <Container
      maxWidth="xl"
      sx={{ py: 4, bgcolor: "#f5f7fa", minHeight: "100vh" }}
    >
      {loading && <LoadingBackDrop openBackDrop={loading} />}

      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                color: "#1a237e",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CarIcon sx={{ fontSize: 40, color: "#1976d2" }} />
              Vehicle Management
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Manage laboratory vehicles, drivers, and their details
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddVehicle}
            sx={{
              bgcolor: "#1976d2",
              "&:hover": { bgcolor: "#1565c0" },
              px: 3,
              py: 1.5,
            }}
          >
            Add New Vehicle
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ borderRadius: 2, bgcolor: "#fff" }}>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    color="textSecondary"
                    variant="caption"
                    fontWeight={600}
                  >
                    TOTAL VEHICLES
                  </Typography>
                  <Typography variant="h3" fontWeight={700} color="#1976d2">
                    {totalVehicles}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: "#1976d2", width: 56, height: 56 }}>
                  <CarIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ borderRadius: 2, bgcolor: "#fff" }}>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    color="textSecondary"
                    variant="caption"
                    fontWeight={600}
                  >
                    ACTIVE VEHICLES
                  </Typography>
                  <Typography variant="h3" fontWeight={700} color="#2e7d32">
                    {activeVehicles}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: "#2e7d32", width: 56, height: 56 }}>
                  <CheckCircleIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ borderRadius: 2, bgcolor: "#fff" }}>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    color="textSecondary"
                    variant="caption"
                    fontWeight={600}
                  >
                    WITH DRIVERS
                  </Typography>
                  <Typography variant="h3" fontWeight={700} color="#ff9800">
                    {vehiclesWithDriver}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: "#ff9800", width: 56, height: 56 }}>
                  <PersonIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ borderRadius: 2, bgcolor: "#fff" }}>
            <CardContent>
              <Box>
                <Typography
                  color="textSecondary"
                  variant="caption"
                  fontWeight={600}
                >
                  VEHICLE TYPES
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {Object.entries(vehicleTypeStats)
                    .slice(0, 3)
                    .map(([type, count]) => (
                      <Chip
                        key={type}
                        label={`${type}: ${count}`}
                        size="small"
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))}
                  {Object.keys(vehicleTypeStats).length > 3 && (
                    <Chip
                      label={`+${Object.keys(vehicleTypeStats).length - 3} more`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter Section */}
      <StyledPaper sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by type, model, number, driver, or contact..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Filter by Status"
              >
                <MenuItem value="all">All Vehicles</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadVehicles}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </StyledPaper>

      {/* Vehicle Table */}
      <StyledPaper>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: "#f5f7fa" }}>
              <TableRow>
                <TableCell>
                  <strong>#</strong>
                </TableCell>
                <TableCell>
                  <strong>Vehicle Type</strong>
                </TableCell>
                <TableCell>
                  <strong>Model</strong>
                </TableCell>
                <TableCell>
                  <strong>Year</strong>
                </TableCell>
                <TableCell>
                  <strong>Number</strong>
                </TableCell>
                <TableCell>
                  <strong>Fuel Type</strong>
                </TableCell>
                <TableCell>
                  <strong>Assigned Driver</strong>
                </TableCell>
                <TableCell>
                  <strong>Driver Contact</strong>
                </TableCell>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
                <TableCell>
                  <strong>Remarks</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedVehicles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <CarIcon sx={{ fontSize: 60, color: "#ccc", mb: 2 }} />
                      <Typography variant="h6" color="textSecondary">
                        No vehicles found
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {searchTerm || filterStatus !== "all"
                          ? "Try adjusting your search or filters"
                          : "Click 'Add New Vehicle' to get started"}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedVehicles.map((vehicle, index) => (
                  <TableRow
                    key={vehicle.id}
                    hover
                    sx={{
                      "&:hover": {
                        bgcolor: "#f5f7fa",
                      },
                    }}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CarIcon fontSize="small" color="primary" />
                        <Typography variant="body2" fontWeight={500}>
                          {vehicle.vehicleType || "N/A"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{vehicle.model || "N/A"}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarIcon fontSize="small" color="action" />
                        {vehicle.year || "N/A"}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <NumberIcon fontSize="small" color="action" />
                        <Typography fontWeight={600} color="primary">
                          {vehicle.number || "N/A"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <FuelIcon fontSize="small" color="action" />
                        <Chip
                          label={vehicle.fuelType || "N/A"}
                          size="small"
                          variant="outlined"
                          sx={{ minWidth: 60 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      {vehicle.assignedDriver ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon fontSize="small" color="primary" />
                          <Typography variant="body2">
                            {vehicle.assignedDriver}
                          </Typography>
                        </Box>
                      ) : (
                        <Chip
                          label="Not Assigned"
                          size="small"
                          variant="outlined"
                          color="default"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {vehicle.driverContact ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {vehicle.driverContact}
                          </Typography>
                        </Box>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        icon={
                          vehicle.status === "active" ? (
                            <CheckCircleIcon />
                          ) : (
                            <CancelIcon />
                          )
                        }
                        label={
                          vehicle.status === "active" ? "Active" : "Inactive"
                        }
                        status={vehicle.status}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={vehicle.remarks || "No remarks"}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            maxWidth: 120,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {vehicle.remarks || "-"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={0.5}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditVehicle(vehicle)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            vehicle.status === "active"
                              ? "Deactivate"
                              : "Activate"
                          }
                        >
                          <IconButton
                            size="small"
                            color={
                              vehicle.status === "active"
                                ? "warning"
                                : "success"
                            }
                            onClick={() => handleToggleStatus(vehicle)}
                          >
                            {vehicle.status === "active" ? (
                              <CancelIcon fontSize="small" />
                            ) : (
                              <CheckCircleIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(vehicle)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredVehicles.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </StyledPaper>

      {/* Add/Edit Vehicle Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{ bgcolor: "#f5f7fa", borderBottom: "1px solid #e0e0e0" }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: "#1976d2" }}>
              {isEditMode ? <EditIcon /> : <AddIcon />}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {isEditMode ? "Edit Vehicle" : "Add New Vehicle"}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {isEditMode
                  ? "Update vehicle and driver information"
                  : "Enter vehicle and driver details"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* Vehicle Details Section */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="primary"
                sx={{ mb: 1 }}
              >
                <CarIcon
                  fontSize="small"
                  sx={{ mr: 1, verticalAlign: "middle" }}
                />
                Vehicle Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Vehicle Type</InputLabel>
                <Select
                  name="vehicleType"
                  value={formData.vehicleType}
                  onChange={handleInputChange}
                  label="Vehicle Type"
                >
                  {vehicleTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Model"
                name="model"
                value={formData.model}
                onChange={handleInputChange}
                required
                placeholder="e.g., Toyota Corolla"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Year"
                name="year"
                type="number"
                value={formData.year}
                onChange={handleInputChange}
                placeholder="e.g., 2022"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Vehicle Number"
                name="number"
                value={formData.number}
                onChange={handleInputChange}
                required
                placeholder="e.g., ABC-1234"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <NumberIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Fuel Type</InputLabel>
                <Select
                  name="fuelType"
                  value={formData.fuelType}
                  onChange={handleInputChange}
                  label="Fuel Type"
                >
                  {fuelTypes.map((fuel) => (
                    <MenuItem key={fuel} value={fuel}>
                      {fuel}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Driver Details Section */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="secondary"
                sx={{ mt: 2, mb: 1 }}
              >
                <PersonIcon
                  fontSize="small"
                  sx={{ mr: 1, verticalAlign: "middle" }}
                />
                Driver Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Assigned Driver"
                name="assignedDriver"
                value={formData.assignedDriver}
                onChange={handleInputChange}
                placeholder="e.g., John Doe"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
                helperText="Name of the driver assigned to this vehicle"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Driver Contact Number"
                name="driverContact"
                value={formData.driverContact}
                onChange={handleInputChange}
                placeholder="e.g., +94 77 123 4567"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
                helperText="Contact number of the assigned driver"
              />
            </Grid>

            {/* Remarks and Status */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                name="remarks"
                value={formData.remarks}
                onChange={handleInputChange}
                placeholder="Additional information about the vehicle"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f5f7fa" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.status === "active"}
                      onChange={handleStatusChange}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {formData.status === "active" ? "Active" : "Inactive"}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formData.status === "active"
                          ? "Vehicle is available for use"
                          : "Vehicle is not available"}
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0" }}>
          <Button onClick={() => setOpenDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSaveVehicle}
            variant="contained"
            startIcon={isEditMode ? <EditIcon /> : <AddIcon />}
            disabled={loading}
          >
            {isEditMode ? "Update Vehicle" : "Add Vehicle"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "#ffebee", color: "#c62828" }}>
          <Box display="flex" alignItems="center" gap={2}>
            <DeleteIcon />
            <Typography variant="h6" fontWeight={600}>
              Confirm Deactivation
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to deactivate this vehicle?
          </Alert>
          {selectedVehicle && (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f5f7fa" }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Vehicle Type
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedVehicle.vehicleType}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Model
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedVehicle.model}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Number
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="primary">
                    {selectedVehicle.number}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Assigned Driver
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedVehicle.assignedDriver || "Not Assigned"}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0" }}>
          <Button onClick={() => setOpenDeleteDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteVehicle}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={loading}
          >
            Deactivate Vehicle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default VehicleManagement;
